import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { devLog } from '@/utils/logger';

import { API, BACKEND_URL } from '@/lib/api';
const AuthContext = createContext();
// Enable sending cookies with every request
axios.defaults.withCredentials = true;

// --- Custom-domain safety net ------------------------------------------------
// Emergent deployments route `/api/*` on the frontend origin to the backend.
// If the app is opened on a custom domain (e.g. k1.valueplusretail.com) but
// the built bundle still references the preview backend URL, every request
// becomes cross-origin and fails CORS / cookie auth. This interceptor detects
// that mismatch at runtime and rewrites such URLs to be RELATIVE so they hit
// the same-origin `/api` proxy — no rebuild required.
(() => {
  if (typeof window === 'undefined' || !BACKEND_URL) return;
  let envHost;
  try { envHost = new URL(BACKEND_URL).hostname; } catch { return; }
  axios.interceptors.request.use((config) => {
    if (window.location.hostname !== envHost && typeof config.url === 'string'
        && config.url.startsWith(BACKEND_URL)) {
      config.url = config.url.slice(BACKEND_URL.length) || '/';
    }
    return config;
  });
})();
// ----------------------------------------------------------------------------

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (err) { devLog('Logout request failed:', err.message); }
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (err) {
      devLog('Auth check failed, user not logged in:', err.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // On mount, try to authenticate via httpOnly cookie (skipped on the public /creators pages and the creator portal /creator/*)
    if (/^\/creators?(\/|$)/.test(window.location.pathname)) setLoading(false);
    else fetchUser();

    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        if (error.response?.status === 401) {
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [fetchUser]);

  const login = async (email, password, coords = null) => {
    const payload = { email, password };
    if (coords && Number.isFinite(coords.latitude) && Number.isFinite(coords.longitude)) {
      payload.latitude = coords.latitude;
      payload.longitude = coords.longitude;
    }
    const response = await axios.post(`${API}/auth/login`, payload);
    const data = response.data;
    // Also set Authorization header as fallback for cross-origin scenarios
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setUser(data.user);
    return data.user;
  };

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
